import { z } from 'zod'

/**
 * Shared wire vocabulary + row schemas for the CPG registry admin endpoints (/v2/cpg/*,
 * docs/cpg/CPG_ADMIN.md). This module is bundled into the frontends via @behuman/core-contract —
 * only *.contract.ts files may cross the package boundary (web's fail-on-server-imports
 * guard), so the cpg_* enum-like value lists are DEFINED here and re-exported by
 * ../cpg-shared for server-side code (the Drizzle schema and the ingestion tasks).
 */

/** License class of an ingested source; extended as new source adapters land. */
export const CPG_LICENSE_CLASS_VALUES = ['public_domain', 'cc_by_nc_nd'] as const
export type CpgLicenseClass = (typeof CPG_LICENSE_CLASS_VALUES)[number]

/**
 * Document registration workflow. Naming rule: `pending_*` = waiting on a machine (a doc
 * stuck in pending_classification is an ops alert, not a review item); `needs_*` = waiting
 * on a human. Lifecycle: pending_classification → needs_registration → active →
 * superseded | withdrawn.
 */
export const CPG_DOCUMENT_STATUS_VALUES = [
  'pending_classification',
  'needs_registration',
  'active',
  'superseded',
  'withdrawn',
] as const
export type CpgDocumentStatus = (typeof CPG_DOCUMENT_STATUS_VALUES)[number]

/** What a cpg_unit chunk is: the retrieval-unit taxonomy. */
export const CPG_UNIT_KIND_VALUES = [
  'recommendation',
  'rationale',
  'clinical',
  'discussion',
] as const
export type CpgUnitKind = (typeof CPG_UNIT_KIND_VALUES)[number]

/**
 * Normalized recommendation strength (static per-source map at ingestion; unknown source
 * code → NULL + sync warning — fail-soft, grades never gate retrieval).
 */
export const CPG_GRADE_VALUES = ['strong', 'conditional', 'expert'] as const
export type CpgGrade = (typeof CPG_GRADE_VALUES)[number]

/** Unit population sex constraint; NULL = applies to any sex. */
export const CPG_UNIT_SEX_VALUES = ['female', 'male'] as const
export type CpgUnitSex = (typeof CPG_UNIT_SEX_VALUES)[number]

/** Which patient data a chart-side xwalk rule matches against. */
export const CPG_XWALK_KIND_VALUES = ['condition', 'observation', 'medication'] as const
export type CpgXwalkKind = (typeof CPG_XWALK_KIND_VALUES)[number]

/** Provenance of a guideline→family assignment. */
export const CPG_ASSIGNED_VIA_VALUES = ['ai', 'manual'] as const
export type CpgAssignedVia = (typeof CPG_ASSIGNED_VIA_VALUES)[number]

/** What a measure names: a risk to flag or a care gap to close (docs/GLOSSARY.md). */
export const CPG_MEASURE_KIND_VALUES = ['risk', 'gap'] as const
export type CpgMeasureKind = (typeof CPG_MEASURE_KIND_VALUES)[number]

/**
 * Measure lifecycle. `active` is what the agent reports against; `retired` is an admin
 * decision, reversible (→ active). Agent proposals never become measure rows on their own —
 * they accumulate in cpg_measure_proposal and only an accepted cluster creates a measure.
 */
export const CPG_MEASURE_STATUS_VALUES = ['active', 'retired'] as const
export type CpgMeasureStatus = (typeof CPG_MEASURE_STATUS_VALUES)[number]

/**
 * Who first wrote the measure: a seed migration, an admin, or an admin accepting a proposal
 * cluster (PD-1716).
 */
export const CPG_MEASURE_ORIGIN_VALUES = ['seed', 'manual', 'clustered'] as const
export type CpgMeasureOrigin = (typeof CPG_MEASURE_ORIGIN_VALUES)[number]

/**
 * An accumulated measure proposal (cpg_measure_proposal, PD-1714): `pending` until PD-1716's
 * clustering run and an admin decide it — `accepted` (a measure now covers it) or
 * `dismissed`. findings.sync writes `pending` only and never changes a decided proposal.
 */
export const CPG_MEASURE_PROPOSAL_STATUS_VALUES = ['pending', 'accepted', 'dismissed'] as const
export type CpgMeasureProposalStatus = (typeof CPG_MEASURE_PROPOSAL_STATUS_VALUES)[number]

/**
 * A proposal cluster (cpg_measure_proposal_cluster, PD-1716). A clustering run writes
 * `recommended` (enough distinct patients, no related measure) or `below-threshold` (every
 * other open cluster) and replaces its own open clusters on the next run; the admin's
 * `accepted` / `dismissed` are final and never touched by a run.
 */
export const CPG_PROPOSAL_CLUSTER_STATUS_VALUES = [
  'recommended',
  'below-threshold',
  'accepted',
  'dismissed',
] as const
export type CpgProposalClusterStatus = (typeof CPG_PROPOSAL_CLUSTER_STATUS_VALUES)[number]

/** The statuses a clustering run writes and may replace — everything not yet decided. */
export const CPG_PROPOSAL_CLUSTER_OPEN_STATUSES = ['recommended', 'below-threshold'] as const

/** Registry tables covered by cpg_registry_audit (values = full table names). */
export const CPG_AUDIT_ENTITY_TYPE_VALUES = [
  'cpg_family',
  'cpg_family_group',
  'cpg_family_patient_xwalk',
  'cpg_document_family',
  'cpg_measure',
  'cpg_measure_proposal_cluster',
] as const
export type CpgAuditEntityType = (typeof CPG_AUDIT_ENTITY_TYPE_VALUES)[number]

/** Audited registry edit kinds. */
export const CPG_AUDIT_ACTION_VALUES = ['insert', 'update', 'delete'] as const
export type CpgAuditAction = (typeof CPG_AUDIT_ACTION_VALUES)[number]

/**
 * Code systems the xwalk rule editor offers (schema allows any uri; these are the ones the
 * matching engine resolves subtrees for — see docs/db/DB_SCHEMA_CPG.md).
 */
export const CPG_XWALK_SYSTEMS = [
  { uri: 'http://hl7.org/fhir/sid/icd-10-cm', label: 'ICD-10-CM' },
  { uri: 'http://snomed.info/sct', label: 'SNOMED CT' },
  { uri: 'http://loinc.org', label: 'LOINC' },
  { uri: 'http://www.nlm.nih.gov/research/umls/rxnorm', label: 'RxNorm' },
  { uri: 'http://www.whocc.no/atc', label: 'ATC' },
] as const

// ---- Shared row schemas ------------------------------------------------------------------

/** One guideline→family assignment as shown in the review UI. */
export const cpgAssignmentSchema = z.object({
  familyId: z.number(),
  slug: z.string(),
  label: z.string(),
  groupLabel: z.string(),
  assignedVia: z.enum(CPG_ASSIGNED_VIA_VALUES),
  /** LLM's one-line reason; null for manual assignments. */
  rationale: z.string().nullable(),
  /** Medplum user reference who confirmed; null = unconfirmed suggestion. */
  confirmedBy: z.string().nullable(),
})
export type CpgAssignment = z.infer<typeof cpgAssignmentSchema>

/** Compact assignment form carried on document list rows (chips). */
export const cpgAssignmentChipSchema = z.object({
  familyId: z.number(),
  slug: z.string(),
  assignedVia: z.enum(CPG_ASSIGNED_VIA_VALUES),
  confirmed: z.boolean(),
})
export type CpgAssignmentChip = z.infer<typeof cpgAssignmentChipSchema>

export const cpgDocumentListItemSchema = z.object({
  id: z.number(),
  /** Owning source's adapter code ('uspstf', …). */
  source: z.string(),
  externalId: z.string(),
  title: z.string(),
  url: z.string(),
  version: z.string(),
  /** ISO date ('YYYY-MM-DD'). */
  publishedAt: z.string().nullable(),
  status: z.enum(CPG_DOCUMENT_STATUS_VALUES),
  lastSeenAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
  families: z.array(cpgAssignmentChipSchema),
})
export type CpgDocumentListItem = z.infer<typeof cpgDocumentListItemSchema>

/** A related document reference (succession links). */
export const cpgDocumentRefSchema = z.object({
  id: z.number(),
  title: z.string(),
  version: z.string(),
  status: z.enum(CPG_DOCUMENT_STATUS_VALUES),
})
export type CpgDocumentRef = z.infer<typeof cpgDocumentRefSchema>

export const cpgUnitViewSchema = z.object({
  id: z.number(),
  kind: z.enum(CPG_UNIT_KIND_VALUES),
  sectionPath: z.string().nullable(),
  gradeOriginal: z.string().nullable(),
  grade: z.enum(CPG_GRADE_VALUES).nullable(),
  body: z.string(),
  ageMin: z.number().nullable(),
  ageMax: z.number().nullable(),
  sex: z.enum(CPG_UNIT_SEX_VALUES).nullable(),
  /**
   * Population requirements (tobacco/pregnant/sexuallyActive: true, bmi: class code).
   * The vocabulary is code-owned by riskFlagsSchema (src/cpg/risk-flags.ts) — kept loose
   * here so new flags don't need a contract change.
   */
  riskFlags: z.record(z.string(), z.union([z.boolean(), z.string()])).nullable(),
})
export type CpgUnitView = z.infer<typeof cpgUnitViewSchema>

export const cpgDocumentDetailSchema = z.object({
  id: z.number(),
  source: z.string(),
  sourceName: z.string(),
  externalId: z.string(),
  title: z.string(),
  url: z.string(),
  version: z.string(),
  publishedAt: z.string().nullable(),
  scrapedAt: z.iso.datetime(),
  status: z.enum(CPG_DOCUMENT_STATUS_VALUES),
  lastSeenAt: z.iso.datetime().nullable(),
  ageMin: z.number().nullable(),
  ageMax: z.number().nullable(),
  /** Predecessor this doc replaces (recorded chain link). */
  supersedes: cpgDocumentRefSchema.nullable(),
  /** Successor that replaced this doc, when one exists. */
  supersededBy: cpgDocumentRefSchema.nullable(),
  /**
   * Auto-detected activation predecessor: the currently ACTIVE doc with the same
   * source + external_id. What activation will supersede unless overridden.
   */
  activationPredecessor: cpgDocumentRefSchema.nullable(),
  assignments: z.array(cpgAssignmentSchema),
  units: z.array(cpgUnitViewSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  updatedBy: z.string().nullable(),
})
export type CpgDocumentDetail = z.infer<typeof cpgDocumentDetailSchema>

export const cpgFamilySchema = z.object({
  id: z.number(),
  groupId: z.number(),
  slug: z.string(),
  label: z.string(),
  description: z.string(),
  active: z.boolean(),
  /** Documents assigned to this family (any status, confirmed or not). */
  documentCount: z.number(),
  /** Patient-xwalk rules targeting this family. */
  ruleCount: z.number(),
  updatedAt: z.iso.datetime(),
  updatedBy: z.string().nullable(),
})
export type CpgFamily = z.infer<typeof cpgFamilySchema>

export const cpgFamilyGroupSchema = z.object({
  id: z.number(),
  slug: z.string(),
  label: z.string(),
  families: z.array(cpgFamilySchema),
})
export type CpgFamilyGroup = z.infer<typeof cpgFamilyGroupSchema>

export const cpgXwalkRuleSchema = z.object({
  id: z.number(),
  familyId: z.number(),
  familySlug: z.string(),
  familyLabel: z.string(),
  kind: z.enum(CPG_XWALK_KIND_VALUES),
  system: z.string(),
  code: z.string(),
  updatedAt: z.iso.datetime(),
  updatedBy: z.string().nullable(),
})
export type CpgXwalkRule = z.infer<typeof cpgXwalkRuleSchema>

/** One named risk or care gap of the measure registry (docs/GLOSSARY.md "Measure"). */
/**
 * One guideline passage a measure was written from (cpg_measure_citation, PD-1712). The unit
 * pins the exact document version; `documentStatus` other than 'active' means the guideline
 * has since been superseded or withdrawn, so the measure is due for review.
 */
export const cpgMeasureCitationSchema = z.object({
  unitId: z.number(),
  /** Source-native passage id (e.g. USPSTF `rec-1981`); null when the adapter has none. */
  unitExternalId: z.string().nullable(),
  unitKind: z.enum(CPG_UNIT_KIND_VALUES),
  /** Grade as published ('A', 'B', …) for recommendation units; null for supporting passages. */
  gradeOriginal: z.string().nullable(),
  sectionPath: z.string().nullable(),
  /** The start of the passage, for display; the full text is on the document. */
  excerpt: z.string(),
  documentId: z.number(),
  documentTitle: z.string(),
  /** Owning source's adapter code ('uspstf', …). */
  source: z.string(),
  version: z.string(),
  publishedAt: z.string().nullable(),
  url: z.string(),
  documentStatus: z.enum(CPG_DOCUMENT_STATUS_VALUES),
})
export type CpgMeasureCitation = z.infer<typeof cpgMeasureCitationSchema>

export const cpgMeasureSchema = z.object({
  id: z.number(),
  /** kebab-case, the agent-facing identity — immutable once created. */
  slug: z.string(),
  label: z.string(),
  kind: z.enum(CPG_MEASURE_KIND_VALUES),
  familyId: z.number(),
  familySlug: z.string(),
  familyLabel: z.string(),
  /** What qualifies a patient — load-bearing text read by the agent. */
  definition: z.string(),
  /** When a finding is high vs medium — read by the agent. */
  gradingRule: z.string(),
  status: z.enum(CPG_MEASURE_STATUS_VALUES),
  origin: z.enum(CPG_MEASURE_ORIGIN_VALUES),
  /** Proposer's one line; null unless the agent proposed the measure. */
  rationale: z.string().nullable(),
  /** The guideline passages the measure was written from; empty = no guideline basis. */
  citations: z.array(cpgMeasureCitationSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  updatedBy: z.string().nullable(),
})
export type CpgMeasure = z.infer<typeof cpgMeasureSchema>

export const cpgAuditEntrySchema = z.object({
  id: z.number(),
  actor: z.string(),
  entityType: z.enum(CPG_AUDIT_ENTITY_TYPE_VALUES),
  entityId: z.number(),
  action: z.enum(CPG_AUDIT_ACTION_VALUES),
  oldValue: z.unknown().nullable(),
  newValue: z.unknown().nullable(),
  createdAt: z.iso.datetime(),
})
export type CpgAuditEntry = z.infer<typeof cpgAuditEntrySchema>

export const cpgSourceSummarySchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  licenseClass: z.enum(CPG_LICENSE_CLASS_VALUES),
  lastSyncAt: z.iso.datetime().nullable(),
  /** Document counts by status. */
  counts: z.object({
    pendingClassification: z.number(),
    needsRegistration: z.number(),
    active: z.number(),
    superseded: z.number(),
    withdrawn: z.number(),
  }),
})
export type CpgSourceSummary = z.infer<typeof cpgSourceSummarySchema>
